/**
 * The speed check, as an operator and a support representative see it.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * `benchmark-telemetry.ts` is the wire schema — what a run *is*. This module is
 * everything around it that both the main process and the renderer need to agree
 * on: how deep a check runs, what it is doing while it runs, what it found that
 * is too specific to upload, and — most of it — what each stage is *called* in
 * front of a customer.
 *
 * ## Naming is a hard requirement, not a preference
 *
 * Every string in here is read aloud on a support call or read over a
 * salesperson's shoulder. So:
 *
 * - **Components are named for what they do, never for what they are.** No model
 *   names, no vendor names, no open-source project names, no quantization or
 *   runtime jargon, anywhere a customer can reach. "Understanding documents",
 *   not the name of the thing doing the understanding.
 * - **No measurement jargon either.** Accuracy is reported as documents split
 *   correctly, documents torn in two, and documents stuck together — never as
 *   precision, recall, or F1, which have been tried on a reader and produced
 *   "I have no idea what this table is saying".
 * - **Speeds are pages per minute.** Never milliseconds per page, never seconds
 *   per page. Extraction additionally carries documents per minute because its
 *   cost is per document rather than per page.
 */

import type { BenchmarkStageId, StoredBenchmarkRun } from './benchmark-telemetry';
import {
  canonicalPerformanceWorkload,
  sharedPerformanceWorkload,
  smallWavePerformanceWorkload,
  timedV7PerformanceWorkload,
  type PerformanceCalibration,
  type PerformanceMeasurement,
} from './performance-score';
import type { SpeedTestWhy } from './speed-test-why';

/**
 * How much work a check does.
 *
 * Two diagnostic depths exist because the same question can be asked with more or less
 * confidence. A support representative is on the telephone and needs an answer
 * inside a coffee break; somebody chasing an intermittent slowdown wants a sample
 * large enough that the answer does not move between runs.
 *
 * A separate performance profile pins one sample set for cross-machine scoring.
 * Its duration remains unestimated until both calibration machines are measured.
 * Both diagnostic depths are minutes. The overnight check runs the
 * quick profile — it is the one whose cost has been checked against a real run, and
 * a check that starts itself has no business being the expensive one.
 *
 * They used to differ only in pages-per-corpus and repeats, and both ran *every*
 * corpus the installation shipped. On 2026-07-28 that was eleven, and a quick
 * check on a fast machine measured 36.6 minutes of wall clock against a promise
 * of four to ten — because every corpus pays for six stages and then pays again
 * for the whole pipeline end to end. Six stages times eleven corpora times two
 * repeats is not a coffee break on any machine, and the slower the machine the
 * worse the lie.
 *
 * So a quick check now measures a *few* corpora rather than all of them. What is
 * measured per corpus is identical, which is what keeps the two depths
 * comparable: the same stages, the same way, on corpora the full check also
 * covers. It is a smaller sample, not a different measurement.
 */
export type SpeedCheckDepth = 'quick' | 'full' | 'performance';

export type SpeedCheckDepthProfile = {
  readonly id: SpeedCheckDepth;
  readonly title: string;
  readonly summary: string;
  /** Roughly how many pages of each sample set to measure. */
  readonly pageBudget: number;
  /**
   * How many sample sets to measure, or null for every one installed.
   *
   * The single biggest lever on how long a check takes, and the one that was
   * missing: depth controlled the pages inside a corpus but never the number of
   * corpora, so both depths always paid for all of them.
   */
  readonly corpusLimit: number | null;
  /** Warm passes of each stage. One pass is not a measurement. */
  readonly warmRepeats: number;
  /** What to tell the operator before they commit to waiting. */
  readonly expectedMinutes: readonly [number, number] | null;
  readonly requiredCorpusIds?: readonly string[];
};

export const speedCheckDepths: Readonly<Record<SpeedCheckDepth, SpeedCheckDepthProfile>> = {
  performance: {
    id: 'performance',
    title: 'Performance test',
    summary:
      'Three minutes after preparation. Compare how many sample pages this computer finishes.',
    pageBudget: 106,
    warmRepeats: 2,
    corpusLimit: 1,
    requiredCorpusIds: ['accounts_payable_small_set'],
    expectedMinutes: [3, 3],
  },
  quick: {
    id: 'quick',
    title: 'Quick check',
    summary: 'Every part of the pipeline, on three sample sets. Use this one on a support call.',
    pageBudget: 4,
    warmRepeats: 2,
    corpusLimit: 3,
    // Measured, not guessed. A run on 2026-07-28 cost 3.33 minutes per corpus at
    // eight pages and two repeats; three corpora at four pages is about a third
    // of that each. The range allows for a machine several times slower.
    //
    // RE-MEASURED 2026-08-08 from parts, not scaled. Two inputs, both measured on the
    // i9-14900K + RTX 4090:
    //
    //   engine cold start to ocrReady && semanticReady   16.5s median of 2
    //   whole-pipeline page cost, warm                   0.8s   (73-76 pages/min,
    //                                                     accounts_payable_small_set,
    //                                                     29 pages / 21 documents)
    //
    // A quick check is 3 corpora, each paying one restart plus a cold pass and two
    // warm passes over 4 pages: 3 x (16.5 + 12 x 0.8) = 78s, about 1.3 minutes here.
    // The old 4-12 was measured on 2026-07-28, before the detection cap and the lane
    // plan, and overstates by roughly 3x.
    //
    // The band is 2-8 rather than 1-2 because this machine is not the floor: the low
    // end is this workstation with a little headroom, the high end a machine roughly
    // six times slower, which is the shape of the range the old band was reaching for.
    // Deliberately still generous — an estimate that runs LONG is a mild annoyance and
    // one that runs SHORT teaches an operator the number is fiction, which is the
    // "never a bare spinner" rule failing from the other side.
    //
    // Measured on ONE corpus. Sets with more pages per document spend proportionally
    // more in extraction, so treat the page cost as an order of magnitude rather than
    // a constant.
    expectedMinutes: [2, 8],
  },
  full: {
    id: 'full',
    title: 'Deeper check',
    summary:
      'Twice the sample sets and twice the pages, so the numbers move around less. Still minutes, not hours.',
    // RE-SCOPED 2026-08-15. This profile was 11 corpora x 20 pages x 3 warm repeats,
    // advertised at 90 to 240 minutes. Nothing on this machine is worth ninety minutes
    // of it, let alone four hours, and the operator's verdict on being shown that band
    // was that it was the dumbest thing they had seen.
    //
    // The reasoning that produced it was: overnight, so a long estimate costs nothing.
    // That is wrong twice. It costs the operator who runs it by hand — this is the
    // only other way to run it — and it is not what a speed check is FOR. A speed
    // check answers "is this workstation slower than it was", and that is a sampling
    // question: more corpora and more pages narrow the error bar with diminishing
    // returns, they do not buy a different answer. Real long-run numbers come from
    // customers actually processing their own documents, which is a far better
    // measurement than any synthetic corpus and costs nobody a dedicated hour.
    //
    // So the target was set first — around five minutes on this workstation, which is
    // roughly twenty on an ordinary one without a large accelerator — and the profile
    // derived to fit it, from the same two measured parts as the quick profile:
    //
    //   engine cold start to ocrReady && semanticReady   16.5s
    //   whole-pipeline page cost, warm                   0.8s
    //
    //   6 corpora x (16.5s + 8 pages x 3 passes x 0.8s) = 214s, about 3.6 minutes here.
    //
    // Against the quick profile that is twice the corpora, twice the pages, and three
    // times the page-passes — a materially steadier sample — for under four minutes.
    pageBudget: 8,
    warmRepeats: 2,
    corpusLimit: 6,
    // DERIVED, not measured end to end: the parts above are measured, the sum is not.
    // The low end is this workstation with headroom; the high end is a machine about
    // five to six times slower, which is the same spread the quick band carries. An
    // estimate that runs long is a mild annoyance and one that runs short teaches an
    // operator the number is fiction, so the band leans long. Re-measure with a real
    // run when a card is free.
    expectedMinutes: [4, 20],
  },
};

/**
 * What each timed stage is called in front of a customer.
 *
 * The `stage` identifiers are the wire schema's and match the lab's vocabulary
 * so field numbers can be laid beside lab numbers. Nothing else here may leak
 * that vocabulary: `fingerprint` is a stage name in a findings document and
 * means nothing to anyone else, so it is shown as finding where documents start.
 */
export type StagePresentation = {
  readonly stage: BenchmarkStageId;
  readonly title: string;
  /** One sentence, plain language, no jargon. Shown under the number. */
  readonly explanation: string;
  /** True when documents per minute is the more meaningful figure to lead with. */
  readonly ratePerDocument: boolean;
};

export const stagePresentation: readonly StagePresentation[] = [
  {
    stage: 'ocr',
    title: 'Reading text',
    explanation:
      'Turning the picture of each page into words the computer can search and pull values from.',
    ratePerDocument: false,
  },
  {
    stage: 'barcode',
    title: 'Reading barcodes',
    explanation: 'Finding and decoding barcodes anywhere on the page.',
    ratePerDocument: false,
  },
  {
    stage: 'fingerprint',
    title: 'Finding where documents start',
    explanation:
      'Deciding which pages begin a new document, so a stack of scans becomes separate documents.',
    ratePerDocument: false,
  },
  {
    stage: 'documentType',
    title: 'Recognizing the kind of document',
    explanation: 'Deciding which of the document kinds set up in a job each document is.',
    ratePerDocument: true,
  },
  {
    stage: 'extraction',
    title: 'Pulling out the values',
    explanation:
      'Reading the fields off a document. This one costs about the same for a long document as a short one, so document length changes it more than anything else.',
    ratePerDocument: true,
  },
] as const;

export const stageTitle = (stage: BenchmarkStageId): string =>
  stagePresentation.find((entry) => entry.stage === stage)?.title ?? stage;

/** Cold and warm, said out loud. They are never averaged together. */
export const phrasing = {
  cold: {
    title: 'First batch after starting',
    explanation:
      'Includes the one-time warm-up Paige pays the first time it reads anything after it starts. It is how long a first batch of the morning takes, not how fast the computer runs.',
  },
  warm: {
    title: 'Once warmed up',
    explanation:
      'How fast the computer runs for the rest of the day. This is the number to compare against.',
  },
} as const;

/**
 * How well a stack of scans was separated back into documents.
 *
 * The sample sets ship with a record of where each document really starts, so
 * this is measured against a known answer rather than estimated. It is
 * deliberately expressed as three counts an operator already understands,
 * because the standard names for them are unreadable to the people who have to
 * act on this screen.
 *
 * This is **not** uploaded. The wire schema carries timings only, and separation
 * accuracy on our own sample data says nothing about the customer's hardware —
 * it is the same on every machine — so it stays local, where it answers "is this
 * installation behaving" rather than feeding the speed database.
 */
export type SeparationAccuracy = {
  /** Document starts found exactly where the sample set says one is. */
  readonly splitCorrectly: number;
  /** Total document starts the sample set contains. */
  readonly expectedDocuments: number;
  /** Splits made in the middle of a document — one document torn into two. */
  readonly tornInTwo: number;
  /** Starts that were missed — two documents left stuck together. */
  readonly stuckTogether: number;
};

/** Everything measured about one sample set that is too specific to upload. */
export type SpeedCheckDatasetDetail = {
  readonly timedWindow?: {
    readonly version: 1 | 2 | 3;
    readonly durationMs: number;
    readonly completedSamples: number;
  };
  readonly localCompletions?: readonly {
    readonly pages: number;
    readonly documents: number;
    readonly nativeLocalOnly: true;
  }[];
  readonly datasetId: string;
  /** The sample set's own name, e.g. "Freight and logistics". */
  readonly title: string;
  readonly pages: number;
  readonly documents: number;
  readonly pagesPerDocument: number;
  readonly separation: SeparationAccuracy | null;
  /** Stages that could not be measured at all, by customer-facing title. */
  readonly unmeasuredStages: readonly string[];
  /** Cold followed by warm passes. Exact physical-page coverage, not extraction accuracy. */
  readonly pageCoverage?: readonly BenchmarkPageCoverage[];
  /** Cold then warm, manifest document grouping/type checks; not field accuracy. */
  readonly documentAccuracy?: readonly BenchmarkDocumentAccuracy[];
  /** Cold then warm; only configured scalar fields, not table extraction. */
  readonly fieldAccuracy?: readonly BenchmarkFieldAccuracy[];
  /** Cold then warm; the product's four line-item columns, with other printed cells counted separately. */
  readonly tableAccuracy?: readonly BenchmarkTableAccuracy[];
  /** Cold then warm helper-dispatch evidence. */
  readonly peerExecution?: readonly BenchmarkPeerExecution[];
};

export type BenchmarkPeerExecution = {
  readonly status: 'no-peers' | 'peers-present' | 'unknown';
  readonly stages: readonly {
    stage: string;
    totalUnits: number;
    localUnits: number;
    peerUnits: number;
  }[];
};

export type BenchmarkTableAccuracy = {
  readonly expectedRows: number;
  readonly correctRows: number;
  readonly missingRows: number;
  readonly extraRows: number;
  readonly wrongCells: number;
  readonly misplacedRows: number;
  readonly unreadDocuments: number;
  readonly unscoredPrintedCells: number;
  readonly missingTruthDocuments: number;
  readonly passed: boolean;
};

export type BenchmarkFieldAccuracy = {
  readonly expected: number;
  readonly correct: number;
  readonly wrong: number;
  readonly missing: number;
  readonly duplicate: number;
  readonly missingTruthDocuments: number;
  readonly passed: boolean;
};

export type BenchmarkDocumentAccuracy = {
  readonly expected: number;
  readonly correctType: number;
  readonly incorrectType: number;
  readonly malformedDocuments: number;
  readonly missingDocuments: number;
  readonly missingTruth: number;
  readonly passed: boolean;
};

export type BenchmarkPageCoverage = {
  readonly expected: number;
  readonly uniqueReturned: number;
  readonly missing: number;
  readonly duplicate: number;
  readonly unexpected: number;
  readonly unidentified: number;
  readonly complete: boolean;
};

/**
 * The local companion to a stored run.
 *
 * Kept beside the run rather than inside it because the run file is, by
 * deliberate design, byte-for-byte what would be uploaded. Anything that is not
 * uploaded must therefore live somewhere else, or that guarantee stops being
 * checkable by reading the file.
 */
export type SpeedCheckDetail = {
  readonly version: 1;
  readonly runId: string;
  readonly capturedAt: string;
  readonly depth: SpeedCheckDepth;
  readonly trigger: SpeedCheckTrigger;
  readonly datasets: readonly SpeedCheckDatasetDetail[];
  /** Local exact workload identity. Absent on legacy runs; not itself a score qualification. */
  readonly workloadFingerprint?: string;
  /** Problems worth showing the operator, already in plain language. */
  readonly notes: readonly string[];
  /** Why the performance test scored what it did (speed-test-why.ts). Absent before October 6, 2026. */
  readonly why?: SpeedTestWhy;
};

/** Who asked for the run. Scheduled runs are held to the idle rules; operator runs are not. */
export type SpeedCheckTrigger = 'operator' | 'scheduled';

/** What the check is doing right now, for the progress display. */
export type SpeedCheckProgress = {
  readonly runId: string;
  readonly depth: SpeedCheckDepth;
  readonly trigger: SpeedCheckTrigger;
  /** Sample set being measured, by its customer-facing title. */
  readonly datasetTitle: string;
  readonly datasetIndex: number;
  readonly datasetCount: number;
  /** Plain-language description of the current step. */
  readonly step: string;
  /** 0 to 1 across the whole run, best effort. */
  readonly fraction: number;
  readonly startedAt: string;
  readonly phase?: 'preparing' | 'measuring';
  readonly phaseElapsedMs?: number;
};

export type SpeedCheckState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running'; readonly progress: SpeedCheckProgress }
  | { readonly kind: 'finished'; readonly runId: string; readonly finishedAt: string }
  | {
      readonly kind: 'stopped';
      readonly reason: string;
      readonly stoppedAt: string;
      /** The test stopped because its sample documents are missing or incomplete. */
      readonly cause?: 'missing-samples';
      /** For missing samples: how they come back (main adds it; see SpeedCheckAvailability.samples). */
      readonly samples?: SpeedTestSamplesRoute;
    };

/**
 * Why a check cannot start right now.
 *
 * An operator pressing the button is told this instead of being refused
 * silently, and the scheduler uses the same assessment so the two can never
 * disagree about what "the machine is free" means.
 */
export type SpeedCheckBlocker =
  | 'capture-in-progress'
  | 'captures-waiting'
  | 'captures-failed'
  | 'recently-active'
  | 'already-running'
  | 'no-sample-documents';

export const blockerPhrasing: Readonly<Record<SpeedCheckBlocker, string>> = {
  'capture-in-progress':
    'Paige is processing documents right now. The speed check waits until that finishes.',
  'captures-waiting': 'There are scans waiting to be processed. Finish or clear them first.',
  'captures-failed':
    'Some scans could not finish. Open Scan jobs to retry them or clear them before running a speed check.',
  'recently-active':
    'Documents were processed here a few minutes ago. Wait a little so the check measures a settled computer.',
  'already-running': 'A speed check is already running.',
  'no-sample-documents':
    'The sample documents that the speed check measures are not installed on this computer.',
};

/**
 * How the speed test's sample documents can come back when they are missing (the
 * `no-sample-documents` blocker). `download`: this version publishes them as the pinned
 * `sample-documents` runtime pack, fetched and then copied into place. `copy`: they are still
 * on this computer (the installed pack, or the app's own copy), so they are only copied back.
 * `reinstall`: this version has no way to get them.
 */
export type SpeedTestSamplesRoute = 'download' | 'copy' | 'reinstall';

export type SpeedCheckAvailability = {
  readonly canRun: boolean;
  readonly blockers: readonly SpeedCheckBlocker[];
  /** Present only while `no-sample-documents` is a blocker. */
  readonly samples?: SpeedTestSamplesRoute;
  /** The performance test first re-checks an AMD or Intel graphics card (a few minutes longer). */
  readonly checksGraphicsCard?: boolean;
};

/**
 * Why Run speed test did not start, one short plain line per blocker (Brandy, October 6, 2026:
 * one generic "close or finish what Paige is working on" was shown for every blocker, even
 * for missing sample documents, where closing anything does nothing). Shown on the speed test
 * card, where the button was pressed.
 */
export const speedTestBlockedLine: Readonly<Record<SpeedCheckBlocker, string>> = {
  'already-running': 'A speed test is already running.',
  'capture-in-progress': 'Paige is reading documents right now. Try again when it finishes.',
  'captures-waiting': "Documents are still waiting to be read. Try again when they're done.",
  'captures-failed':
    "An import didn't finish. Use Retry unfinished import in the documents list, then try again.",
  'recently-active': 'Paige just finished reading documents. Try again in a few minutes.',
  'no-sample-documents': 'The speed test needs its sample documents.',
};

/** The missing-samples line when nothing in this version can get them back. */
export const speedTestSamplesReinstallLine =
  'The speed test needs its sample documents. Reinstall Paige to get them back.';

/** Only for a blocker this version does not know. */
export const speedTestBlockedFallback =
  'A speed test cannot start right now. Close or finish what Paige is working on and try again.';

/** Blockers a test that pauses imports clears by itself (main's `mayPause`). */
export const speedTestPausableBlockers: readonly SpeedCheckBlocker[] = [
  'capture-in-progress',
  'captures-waiting',
  'captures-failed',
  'recently-active',
];

/**
 * The blockers really in the way of a start, most decisive first: the ones a refused start names (and, the first of
 * them, what telemetry's `speed-test.refused` is). Pausing imports clears the import blockers.
 */
export function speedTestRefusalBlockers(
  availability: Pick<SpeedCheckAvailability, 'blockers'>,
  { pausing = false }: { pausing?: boolean } = {},
): SpeedCheckBlocker[] {
  const order: readonly SpeedCheckBlocker[] = [
    'already-running',
    'no-sample-documents',
    'capture-in-progress',
    'captures-waiting',
    'captures-failed',
    'recently-active',
  ];
  return order.filter(
    (blocker) =>
      availability.blockers.includes(blocker) &&
      !(pausing && speedTestPausableBlockers.includes(blocker)),
  );
}

/**
 * What a refused start says: the line of each blocker that is really in the way, most decisive
 * first. A test already running is the whole answer. Pausing imports clears the import blockers,
 * so they are not named then. Unknown blockers, or none, fall back to the old generic line.
 */
export function speedTestRefusal(
  availability: Pick<SpeedCheckAvailability, 'blockers' | 'samples'>,
  { pausing = false }: { pausing?: boolean } = {},
): string {
  const present = speedTestRefusalBlockers(availability, { pausing });
  if (present[0] === 'already-running') return speedTestBlockedLine['already-running'];
  const lines = present.map((blocker) =>
    blocker === 'no-sample-documents' && availability.samples === 'reinstall'
      ? speedTestSamplesReinstallLine
      : speedTestBlockedLine[blocker],
  );
  return lines.length ? lines.join(' ') : speedTestBlockedFallback;
}

/** The overnight schedule, as the operator controls it. */
export type SpeedCheckSchedulePreferences = {
  /** Off leaves the manual button working; only the unattended run stops. */
  readonly enabled: boolean;
};

export type SpeedCheckScheduleState = {
  readonly preferences: SpeedCheckSchedulePreferences;
  /** The machine's own overnight window, in local time, e.g. "1:40 AM to 3:10 AM". */
  readonly windowLabel: string;
  readonly lastCompletedAt: string | null;
  /** Why the last opportunity was passed over, in plain language. Null when it ran. */
  readonly lastSkipReason: string | null;
};

/** Pages per minute, formatted the one way this product formats it. */
/**
 * Whole pages per minute, because the tenth is noise nobody reads.
 *
 * A tenth of a page per minute is well inside the spread between two repeats of
 * the same measurement — the run this was changed for reported 21.7 with its own
 * passes ranging over a point either side. Printing it invites a reader to
 * compare two numbers that do not differ. Below ten a tenth is a tenth of the
 * whole figure, so it stays.
 */
export const formatPagesPerMinute = (value: number): string =>
  `${value >= 10 ? Math.round(value).toLocaleString() : value.toFixed(1)} pages/min`;

export const formatDocumentsPerMinute = (value: number): string =>
  `${value >= 100 ? Math.round(value).toLocaleString() : value.toFixed(1)} documents/min`;

/**
 * A sample set's title, from its folder name.
 *
 * The folders are named for the vertical they were generated for, which is
 * already the right thing to show — it just needs to stop looking like a
 * database key.
 */
export const datasetTitle = (datasetId: string): string => {
  const words = datasetId
    .replace(/_small_set$/, '')
    .split('_')
    .filter(Boolean);
  if (words.length === 0) return datasetId;
  return words
    .map((word, index) => (index === 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
};

/**
 * The headline number a run is judged on: composed, warmed-up pages per minute
 * pooled over every sample set.
 *
 * Composed rather than per-stage because stages contend for the same hardware
 * rather than composing — the lab measured that adding the stage rates up badly
 * overestimates the whole — so the only honest end-to-end figure is the one that
 * was measured end to end. Warm rather than cold because a cold-inclusive figure
 * describes a first batch, not a throughput.
 *
 * Returns null when a run measured nothing composed, which a caller must show as
 * "not measured" rather than as zero.
 */
export const overallPagesPerMinute = (entry: StoredBenchmarkRun): number | null => {
  const measured = entry.run.datasets
    .map((dataset) => dataset.composite.warm)
    .filter((value): value is NonNullable<typeof value> => value !== null && value.wallClockMs > 0);
  if (measured.length === 0) return null;
  const pages = measured.reduce((sum, value) => sum + value.pages, 0);
  const wallClockMs = measured.reduce((sum, value) => sum + value.wallClockMs, 0);
  if (wallClockMs === 0) return null;
  return Math.round(((pages * 60_000) / wallClockMs) * 10) / 10;
};

/**
 * The two job shapes a customer actually buys, and what each one runs at.
 *
 * One number for the whole pipeline undersells the product badly. It is the
 * speed of the *most* automatic setup Paige can be asked for — every page
 * read, every boundary worked out, every kind recognised, every field pulled off
 * the page — and quoting it alone answers "how fast is Paige" with its
 * slowest configuration. A job that splits on barcodes and takes its field values
 * from them runs about six times faster on the same machine, and it is a common
 * setup, not a contrived one.
 *
 * ## Why the slowest stage, and not the sum of them
 *
 * The stages overlap. Adding up each stage's time per page and inverting it
 * predicted 10 pages a minute on the run this was written against, where the
 * measured end-to-end figure was 21.7 — wrong by a factor of two, because that
 * arithmetic assumes a page waits for every stage in turn. A pipeline's
 * throughput is set by its slowest stage; the others hide behind it.
 *
 * Against the same run, the slowest stage was extraction at 23.2 and the measured
 * pipeline 21.7, which is 94% of it. So a shape's throughput is its slowest
 * stage, discounted by the efficiency this machine's own full run demonstrated.
 * The discount is measured here rather than assumed, which is what keeps every
 * shape honest on hardware that overlaps differently — and what makes the full
 * shape reproduce the measured number instead of merely resembling it.
 */
export type JobShape = {
  readonly id: 'barcode' | 'automatic';
  readonly title: string;
  readonly explanation: string;
  readonly stages: readonly BenchmarkStageId[];
};

export const jobShapes: readonly JobShape[] = [
  {
    id: 'barcode',
    title: 'Split and filled by barcode',
    explanation:
      'Every page read for text, documents split where their barcodes say to, and field values taken straight off those barcodes.',
    // No boundary work and no reading values off the page: the barcode already
    // said where the document starts and what belongs to it.
    stages: ['ocr', 'barcode'],
  },
  {
    id: 'automatic',
    title: 'Worked out automatically',
    explanation:
      'Every page read, where each document starts worked out from the pages themselves, the kind of document recognised, and every field value read off the page.',
    stages: ['ocr', 'barcode', 'fingerprint', 'documentType', 'extraction'],
  },
] as const;

export const jobShapeThroughput = (
  entry: StoredBenchmarkRun,
): readonly {
  shape: JobShape;
  pagesPerMinute: number;
}[] => {
  const rates = new Map(stageThroughput(entry).map((row) => [row.stage, row.pagesPerMinute]));
  const everyStage = jobShapes.find((shape) => shape.id === 'automatic')?.stages ?? [];
  const measuredAll = everyStage
    .map((stage) => rates.get(stage))
    .filter((rate): rate is number => rate !== undefined);
  if (measuredAll.length !== everyStage.length) return [];

  // The discount this machine's own pipeline earned, measured rather than assumed.
  const endToEnd = overallPagesPerMinute(entry);
  const slowestOfAll = Math.min(...measuredAll);
  const efficiency =
    endToEnd !== null && slowestOfAll > 0 ? Math.min(1, endToEnd / slowestOfAll) : 1;

  return jobShapes
    .map((shape) => {
      const measured = shape.stages
        .map((stage) => rates.get(stage))
        .filter((rate): rate is number => rate !== undefined);
      if (measured.length !== shape.stages.length) return null;
      return { shape, pagesPerMinute: Math.min(...measured) * efficiency };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);
};

/** A day's work at a measured rate. Eight hours, because that is a shift. */
export const pagesInAShift = (pagesPerMinute: number): number =>
  Math.round(pagesPerMinute * 60 * 8);

/**
 * Every stage's throughput across everything a run measured, in pipeline order.
 *
 * The card led with one number — the whole pipeline — and then spent a paragraph
 * qualifying it. But the run already measures each stage separately, and those
 * are the numbers that answer the question somebody runs a speed check to ask:
 * not "is this slow" but "what is slow". They were being collected and thrown
 * away at the screen.
 */
export const stageThroughput = (
  entry: StoredBenchmarkRun,
): readonly {
  stage: BenchmarkStageId;
  title: string;
  pagesPerMinute: number;
  documentsPerMinute: number;
  ratePerDocument: boolean;
}[] =>
  stagePresentation
    .map((presented) => {
      const measured = entry.run.datasets
        .flatMap((dataset) => dataset.stages)
        .filter((row) => row.stage === presented.stage)
        .map((row) => row.warm)
        .filter(
          (value): value is NonNullable<typeof value> => value !== null && value.wallClockMs > 0,
        );
      if (measured.length === 0) return null;
      const wallClockMs = measured.reduce((sum, value) => sum + value.wallClockMs, 0);
      if (wallClockMs === 0) return null;
      const pages = measured.reduce((sum, value) => sum + value.pages, 0);
      const documents = measured.reduce((sum, value) => sum + value.documents, 0);
      return {
        stage: presented.stage,
        title: presented.title,
        pagesPerMinute: (pages * 60_000) / wallClockMs,
        documentsPerMinute: (documents * 60_000) / wallClockMs,
        ratePerDocument: presented.ratePerDocument,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);

/**
 * Pages per document across everything a run measured.
 *
 * Always shown beside a throughput, because throughput without it is
 * misleading: the cost of understanding a document falls almost entirely on the
 * document rather than on its pages, so a set of one-page documents makes the
 * same machine look several times slower than a set of ten-page ones.
 */
export const overallPagesPerDocument = (entry: StoredBenchmarkRun): number | null => {
  const pages = entry.run.datasets.reduce((sum, dataset) => sum + dataset.dataset.pages, 0);
  const documents = entry.run.datasets.reduce((sum, dataset) => sum + dataset.dataset.documents, 0);
  if (documents === 0) return null;
  return Math.round((pages / documents) * 100) / 100;
};

/**
 * How this run compares with the machine's own settled history.
 *
 * The comparison is against the **median** of earlier runs rather than the best
 * one, because a best-ever run is by definition the luckiest draw and comparing
 * every later run against it manufactures a decline that is not there.
 *
 * Runs must carry matching local workload fingerprints. Similar document length
 * alone cannot distinguish different files, schemas, readers or repeat settings.
 */
export type SpeedComparison = {
  readonly current: number;
  readonly baseline: number;
  readonly baselineRuns: number;
  /** Negative means slower than the baseline. */
  readonly percentChange: number;
  readonly verdict: 'slower' | 'steady' | 'faster';
  readonly sentence: string;
};

/** One qualification gate shared by machine ratings and same-machine trends. */
const qualifiedBenchmarkIdentity = (
  candidate: StoredBenchmarkRun,
  details: readonly SpeedCheckDetail[],
): string | null => {
  const matches = details.filter((detail) => detail.runId === candidate.run.runId);
  if (matches.length !== 1 || matches[0].capturedAt !== candidate.run.capturedAt) return null;
  const fingerprint = matches[0].workloadFingerprint;
  if (!fingerprint || !/^[a-f0-9]{64}$/.test(fingerprint)) return null;
  if (
    new Set(candidate.run.datasets.map((dataset) => dataset.dataset.id)).size !==
    candidate.run.datasets.length
  )
    return null;
  if (
    !candidate.run.datasets.length ||
    candidate.run.datasets.some(
      (dataset) => !dataset.composite.warm || dataset.composite.warm.failures !== 0,
    )
  )
    return null;
  for (const dataset of candidate.run.datasets) {
    const records = matches[0].datasets.filter((detail) => detail.datasetId === dataset.dataset.id);
    if (records.length !== 1) return null;
    const coverage = records[0].pageCoverage;
    const window = records[0].timedWindow;
    if (window) {
      const measured = dataset.composite.warm!;
      const count = window.completedSamples;
      const validWindow =
        (window.version === 1 && window.durationMs === 120_000) ||
        ((window.version === 2 || window.version === 3) && window.durationMs === 180_000);
      if (window.version === 3) {
        const finished = records[0].localCompletions;
        if (
          matches[0].depth !== 'performance' ||
          !validWindow ||
          dataset.composite.cold !== null ||
          measured.wallClockMs !== window.durationMs ||
          measured.failures !== 0 ||
          !Number.isSafeInteger(count) ||
          count < 0 ||
          !finished ||
          finished.length !== count ||
          finished.some(
            (item) =>
              item.nativeLocalOnly !== true ||
              item.documents !== 1 ||
              !Number.isSafeInteger(item.pages) ||
              item.pages < 1,
          ) ||
          finished.reduce((sum, item) => sum + item.pages, 0) !== measured.pages ||
          finished.length !== measured.documents ||
          measured.pages !== dataset.dataset.pages ||
          measured.documents !== dataset.dataset.documents
        )
          return null;
        continue;
      }
      if (
        matches[0].depth !== 'performance' ||
        !validWindow ||
        !Number.isSafeInteger(count) ||
        count < 0 ||
        dataset.composite.cold !== null ||
        measured.wallClockMs !== window.durationMs ||
        measured.failures !== 0 ||
        measured.pages !== dataset.dataset.pages ||
        measured.documents !== dataset.dataset.documents ||
        !Number.isSafeInteger(measured.pages) ||
        measured.pages < 0 ||
        !Number.isSafeInteger(measured.documents) ||
        measured.documents < 0 ||
        !coverage ||
        coverage.length !== count ||
        coverage.some(
          (pass) =>
            pass.complete !== true ||
            !Number.isSafeInteger(pass.expected) ||
            pass.expected < 1 ||
            pass.uniqueReturned !== pass.expected ||
            pass.missing !== 0 ||
            pass.duplicate !== 0 ||
            pass.unexpected !== 0 ||
            pass.unidentified !== 0,
        ) ||
        coverage.reduce((sum, pass) => sum + pass.expected, 0) !== measured.pages
      )
        return null;
      const peers = records[0].peerExecution;
      if (
        !peers ||
        peers.length !== count ||
        peers.some(
          (pass) =>
            pass.status !== 'no-peers' ||
            !Array.isArray(pass.stages) ||
            !pass.stages.some((stage) => stage.stage === 'ocr') ||
            !pass.stages.some((stage) => stage.stage === 'extraction') ||
            new Set(pass.stages.map((stage) => stage.stage)).size !== pass.stages.length ||
            pass.stages.some(
              (stage) =>
                !['ocr', 'boundary', 'extraction'].includes(stage.stage) ||
                !Number.isSafeInteger(stage.totalUnits) ||
                stage.totalUnits < 1 ||
                stage.localUnits !== stage.totalUnits ||
                stage.peerUnits !== 0,
            ),
        )
      )
        return null;
      continue;
    }
    const repeats = dataset.composite.warm?.repeats ?? 1;
    if (!Number.isSafeInteger(repeats) || repeats < 2) return null;
    const warm = dataset.composite.warm!,
      cold = dataset.composite.cold;
    // Execution receipts must describe the same timed work. Never use a fast
    // partial/duplicated total or an absent cold pass to qualify a trend.
    if (
      !cold ||
      cold.failures !== 0 ||
      cold.pages !== dataset.dataset.pages ||
      warm.pages !== dataset.dataset.pages * repeats ||
      !Number.isFinite(cold.wallClockMs) ||
      cold.wallClockMs <= 0 ||
      !Number.isFinite(warm.wallClockMs) ||
      warm.wallClockMs <= 0
    )
      return null;
    if (
      !coverage ||
      coverage.length !== repeats + 1 ||
      coverage.some(
        (pass) =>
          pass.complete !== true ||
          !Number.isSafeInteger(pass.expected) ||
          pass.expected < 1 ||
          pass.expected !== dataset.dataset.pages ||
          pass.uniqueReturned !== pass.expected ||
          pass.missing !== 0 ||
          pass.duplicate !== 0 ||
          pass.unexpected !== 0 ||
          pass.unidentified !== 0,
      )
    )
      return null;
    const peers = records[0].peerExecution;
    if (
      !peers ||
      peers.length !== repeats + 1 ||
      peers.some((pass) => {
        if (pass.status !== 'no-peers' || !Array.isArray(pass.stages)) return true;
        const names = pass.stages.map((stage) => stage.stage);
        return (
          new Set(names).size !== names.length ||
          !names.includes('ocr') ||
          !names.includes('extraction') ||
          pass.stages.some(
            (stage) =>
              !['ocr', 'boundary', 'extraction'].includes(stage.stage) ||
              !Number.isSafeInteger(stage.totalUnits) ||
              stage.totalUnits < 1 ||
              stage.localUnits !== stage.totalUnits ||
              stage.peerUnits !== 0,
          )
        );
      })
    )
      return null;
  }
  return fingerprint;
};

/** Derive scoring inputs from saved evidence, never caller-supplied success flags.
 * Diagnostic checks remain viewable but cannot substitute for the pinned profile.
 * This qualifies a measurement; it neither creates calibration nor rates a machine. */
export const performanceMeasurementForRun = (
  entry: StoredBenchmarkRun,
  details: readonly SpeedCheckDetail[],
): PerformanceMeasurement | null => {
  const fingerprint = qualifiedBenchmarkIdentity(entry, details);
  if (!fingerprint) return null;
  const detail = details.find((value) => value.runId === entry.run.runId)!;
  const dataset = entry.run.datasets[0];
  if (
    !dataset.composite.warm ||
    detail.depth !== 'performance' ||
    entry.run.datasets.length !== 1 ||
    detail.datasets.length !== 1 ||
    dataset.dataset.id !== 'accounts_payable_small_set' ||
    (!detail.datasets[0].timedWindow &&
      dataset.composite.warm?.repeats !== speedCheckDepths.performance.warmRepeats)
  )
    return null;
  const rate = (dataset.composite.warm.pages * 60_000) / dataset.composite.warm.wallClockMs;
  if (!Number.isFinite(rate) || rate < 0 || (rate === 0 && !detail.datasets[0].timedWindow))
    return null;
  const sameWorkAs = processorPacedSmallWaveWorkload(fingerprint, entry, detail);
  return {
    workloadFingerprint: fingerprint,
    mode: 'local',
    complete: true,
    warmPagesPerMinute: rate,
    ...(sameWorkAs ? { sameWorkAs } : {}),
  };
};

/**
 * A v7 timed test that understood its documents on the processor did the small-wave (v8)
 * test's exact work, so its saved result still compares; its saved record is not rewritten.
 *
 * The v7 identity did not say how the sample files were grouped. A test that understood on the
 * processor (`acceleration.extraction.active === "cpu"`, the same recorded value the test chose
 * its waves by) asked for 2 files then 8 at a time with the processor's request options; one
 * that understood on a graphics card asked for 2 then 40. The small-wave test changed only the
 * card's grouping (to 2 then 8) and left the processor's requests as they were, word for word
 * (`speedTestWavePolicy` in main/benchmark-import.ts; tests/benchmark/speed-test-waves.test.mjs
 * holds both to that and rehashes the sample bytes to both identities). So only the processor's
 * v7 results carry over. A card's v7 result measured other work and stays unrated against a
 * small-wave reference. Never inferred from throughput or hardware names.
 */
export function processorPacedSmallWaveWorkload(
  fingerprint: string,
  entry: StoredBenchmarkRun,
  detail: SpeedCheckDetail,
): string | undefined {
  return canonicalPerformanceWorkload(fingerprint) === timedV7PerformanceWorkload &&
    detail.depth === 'performance' &&
    detail.datasets[0]?.timedWindow?.version === 3 &&
    entry.run.acceleration?.extraction?.active === 'cpu'
    ? smallWavePerformanceWorkload
    : undefined;
}

export type LatestPerformanceMeasurement =
  | { readonly kind: 'untested' }
  | { readonly kind: 'unavailable' }
  | {
      readonly kind: 'ready';
      readonly runId: string;
      readonly capturedAt: string;
      readonly measurement: PerformanceMeasurement;
    };

/** Local calibration provenance. Keep the receipts, not caller-supplied scores.
 * This record never belongs in benchmark telemetry. */
export type PerformanceCalibrationRecord = {
  readonly schemaVersion: 1;
  readonly version: string;
  readonly lower: { readonly entry: StoredBenchmarkRun; readonly detail: SpeedCheckDetail };
  readonly upper: { readonly entry: StoredBenchmarkRun; readonly detail: SpeedCheckDetail };
};

/** Requalify serialized evidence on every load. A stale/partial/assisted anchor
 * must not become trusted merely because a previous process saved it. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function performanceCalibrationFromRecord(value: unknown): PerformanceCalibration | null {
  try {
    if (!value || typeof value !== 'object') return null;
    const record = value as PerformanceCalibrationRecord;
    if (
      record.schemaVersion !== 1 ||
      typeof record.version !== 'string' ||
      !record.version.trim() ||
      record.version.length > 100
    )
      return null;
    const lowerRun = record.lower.entry.run,
      upperRun = record.upper.entry.run;
    if (
      ![lowerRun.runId, upperRun.runId, lowerRun.machineId, upperRun.machineId].every(
        (id) => typeof id === 'string' && id.trim().length > 0,
      ) ||
      lowerRun.runId === upperRun.runId ||
      lowerRun.machineId === upperRun.machineId ||
      !Number.isFinite(Date.parse(lowerRun.capturedAt)) ||
      !Number.isFinite(Date.parse(upperRun.capturedAt))
    )
      return null;
    const lower = performanceMeasurementForRun(record.lower.entry, [record.lower.detail]);
    const upper = performanceMeasurementForRun(record.upper.entry, [record.upper.detail]);
    // The same identity, or a processor-paced v7 run beside a small-wave run (the same work).
    const workloadFingerprint = lower && upper ? sharedPerformanceWorkload(lower, upper) : null;
    if (
      !lower ||
      !upper ||
      workloadFingerprint === null ||
      upper.warmPagesPerMinute <= lower.warmPagesPerMinute
    )
      return null;
    return {
      version: record.version,
      workloadFingerprint,
      lowerPagesPerMinute: lower.warmPagesPerMinute,
      upperPagesPerMinute: upper.warmPagesPerMinute,
    };
  } catch {
    // Disk data is untrusted, including malformed nested receipt shapes.
    return null;
  }
}

export function createPerformanceCalibrationRecord(
  version: string,
  lower: StoredBenchmarkRun,
  lowerDetails: readonly SpeedCheckDetail[],
  upper: StoredBenchmarkRun,
  upperDetails: readonly SpeedCheckDetail[],
): PerformanceCalibrationRecord | null {
  if (
    !performanceMeasurementForRun(lower, lowerDetails) ||
    !performanceMeasurementForRun(upper, upperDetails)
  )
    return null;
  const record: PerformanceCalibrationRecord = {
    schemaVersion: 1,
    version,
    lower: {
      entry: lower,
      detail: lowerDetails.find((detail) => detail.runId === lower.run.runId)!,
    },
    upper: {
      entry: upper,
      detail: upperDetails.find((detail) => detail.runId === upper.run.runId)!,
    },
  };
  // Detach from live history objects so later refreshes cannot rewrite an anchor.
  return performanceCalibrationFromRecord(record) ? structuredClone(record) : null;
}

/** Select the latest dedicated test, not the fastest or last successful one.
 * A newer failed test must not leave an older healthy rating looking current. */
export const latestPerformanceMeasurement = (
  history: readonly StoredBenchmarkRun[],
  details: readonly SpeedCheckDetail[],
): LatestPerformanceMeasurement => {
  const tests = details.filter((detail) => detail.depth === 'performance');
  if (!tests.length) return { kind: 'untested' };
  // An undated result cannot be safely ordered behind a known good result.
  if (tests.some((test) => !Number.isFinite(Date.parse(test.capturedAt))))
    return { kind: 'unavailable' };
  const ordered = [...tests].sort(
    (left, right) => Date.parse(right.capturedAt) - Date.parse(left.capturedAt),
  );
  const latest = ordered[0];
  if (ordered.length > 1 && Date.parse(ordered[1].capturedAt) === Date.parse(latest.capturedAt))
    return { kind: 'unavailable' };
  const matches = history.filter((entry) => entry.run.runId === latest.runId);
  if (matches.length !== 1) return { kind: 'unavailable' };
  const measurement = performanceMeasurementForRun(matches[0], details);
  return measurement
    ? { kind: 'ready', runId: latest.runId, capturedAt: latest.capturedAt, measurement }
    : { kind: 'unavailable' };
};

export const compareWithHistory = (
  entry: StoredBenchmarkRun,
  history: readonly StoredBenchmarkRun[],
  details: readonly SpeedCheckDetail[] = [],
): SpeedComparison | null => {
  const identity = (candidate: StoredBenchmarkRun) =>
    qualifiedBenchmarkIdentity(candidate, details);
  const workload = identity(entry);
  if (!workload) return null;
  const current = overallPagesPerMinute(entry);
  const currentPagesPerDocument = overallPagesPerDocument(entry);
  if (current === null || currentPagesPerDocument === null) return null;

  const earlier = history
    // Ambiguous duplicate records are not independent measurements, even when
    // their values happen to match. Require one stored entry for each run ID.
    .filter(
      (candidate) =>
        history.filter((other) => other.run.runId === candidate.run.runId).length === 1,
    )
    .filter((candidate) => candidate.run.runId !== entry.run.runId)
    .filter((candidate) => candidate.run.capturedAt < entry.run.capturedAt)
    .filter(
      (candidate) =>
        candidate.run.machineId === entry.run.machineId && identity(candidate) === workload,
    )
    .map((candidate) => ({
      rate: overallPagesPerMinute(candidate),
      pagesPerDocument: overallPagesPerDocument(candidate),
    }))
    .filter(
      (candidate): candidate is { rate: number; pagesPerDocument: number } =>
        candidate.rate !== null &&
        candidate.pagesPerDocument !== null &&
        // Within a fifth of the same document length. Beyond that the two runs
        // measured different work and the difference is not about the machine.
        Math.abs(candidate.pagesPerDocument - currentPagesPerDocument) <=
          currentPagesPerDocument * 0.2,
    )
    .map((candidate) => candidate.rate)
    .sort((left, right) => left - right);

  // Two earlier runs is the floor for a baseline: one earlier run is a
  // comparison against a single draw, which is the thing this whole feature
  // exists to stop people doing.
  if (earlier.length < 2) return null;
  const middle = Math.floor(earlier.length / 2);
  const baseline =
    earlier.length % 2 === 1 ? earlier[middle] : (earlier[middle - 1] + earlier[middle]) / 2;
  if (baseline <= 0) return null;

  const percentChange = Math.round(((current - baseline) / baseline) * 100);
  // Ten per cent, because run-to-run spread on a quiet machine is already worth
  // several per cent and a threshold under the noise floor cries wolf.
  const verdict = percentChange <= -10 ? 'slower' : percentChange >= 10 ? 'faster' : 'steady';
  const sentence =
    verdict === 'slower'
      ? `This computer is running about ${Math.abs(percentChange)}% slower than it usually does.`
      : verdict === 'faster'
        ? `This computer is running about ${percentChange}% faster than it usually does.`
        : 'This computer is running at its usual speed.';
  return {
    current,
    baseline: Math.round(baseline * 10) / 10,
    baselineRuns: earlier.length,
    percentChange,
    verdict,
    sentence,
  };
};
